import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import Modal from "@/components/base/Modal";
import { Field, Input, Select, Textarea } from "@/components/base/FormControls";
import { useAgenceData } from "@/pages/agence/data/AgenceDataProvider";
import {
  AGENCE_BIEN_TYPE_LABELS,
  AGENCE_INTERVENTION_PRIORITE_OPTIONS,
  AGENCE_INTERVENTION_STATUT_OPTIONS,
  type AgenceIntervention,
  type AgenceInterventionInput,
  type AgenceInterventionPriorite,
  type AgenceInterventionStatut,
} from "@/pages/agence/data/types";
import { useProprietaires } from "@/pages/agence/proprietaires/ProprietairesProvider";
import { proprietaireNomComplet } from "@/pages/agence/proprietaires/types";

interface InterventionFormProps {
  open: boolean;
  onClose: () => void;
  defaultProprietaireId?: string;
  defaultImmeubleId?: string;
  defaultBienId?: string;
  initial?: AgenceIntervention;
}

interface FormState {
  proprietaireId: string;
  immeubleId: string;
  bienId: string;
  probleme: string;
  description: string;
  priorite: AgenceInterventionPriorite;
  statut: AgenceInterventionStatut;
  cout: string;
  prestataire: string;
  date: string;
}

const EMPTY_STATE: FormState = {
  proprietaireId: "",
  immeubleId: "",
  bienId: "",
  probleme: "",
  description: "",
  priorite: "normale",
  statut: "nouveau",
  cout: "",
  prestataire: "",
  date: "",
};

const MAX_PHOTOS = 6;

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(new Error("Lecture du fichier impossible"));
    reader.readAsDataURL(file);
  });
}

export default function InterventionForm({
  open,
  onClose,
  defaultProprietaireId,
  defaultImmeubleId,
  defaultBienId,
  initial,
}: InterventionFormProps) {
  const { immeubles, biens, getBien, addIntervention, updateIntervention } = useAgenceData();
  const { proprietaires } = useProprietaires();

  const [form, setForm] = useState<FormState>(EMPTY_STATE);
  const [photos, setPhotos] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        proprietaireId: initial.proprietaireId,
        immeubleId: initial.immeubleId,
        bienId: initial.bienId,
        probleme: initial.probleme,
        description: initial.description,
        priorite: initial.priorite,
        statut: initial.statut,
        cout: initial.cout ? String(initial.cout) : "",
        prestataire: initial.prestataire,
        date: initial.date,
      });
      setPhotos(initial.photos);
      setErrors({});
      return;
    }
    setForm(() => {
      const base: FormState = { ...EMPTY_STATE, date: new Date().toISOString().slice(0, 10) };
      if (defaultBienId) {
        const bien = getBien(defaultBienId);
        if (bien) {
          base.bienId = bien.id;
          base.immeubleId = bien.immeubleId;
          base.proprietaireId = bien.proprietaireId;
        }
      }
      if (defaultImmeubleId && !base.immeubleId) {
        base.immeubleId = defaultImmeubleId;
        const immeuble = immeubles.find((item) => item.id === defaultImmeubleId);
        if (immeuble) base.proprietaireId = immeuble.proprietaireId;
      }
      if (defaultProprietaireId && !base.proprietaireId) base.proprietaireId = defaultProprietaireId;
      return base;
    });
    setPhotos([]);
    setErrors({});
  }, [open, defaultProprietaireId, defaultImmeubleId, defaultBienId, getBien, immeubles]);

  const proprietaireOptions = useMemo(
    () => proprietaires.map((item) => ({ value: item.id, label: proprietaireNomComplet(item) })),
    [proprietaires]
  );

  const immeubleOptions = useMemo(
    () =>
      immeubles
        .filter((immeuble) => !form.proprietaireId || immeuble.proprietaireId === form.proprietaireId)
        .map((immeuble) => ({ value: immeuble.id, label: immeuble.nom })),
    [immeubles, form.proprietaireId]
  );

  const bienOptions = useMemo(
    () =>
      biens
        .filter((bien) => !form.immeubleId || bien.immeubleId === form.immeubleId)
        .map((bien) => ({
          value: bien.id,
          label: `${AGENCE_BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` · ${bien.reference}` : ""}`,
        })),
    [biens, form.immeubleId]
  );

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value } as FormState;
      if (key === "proprietaireId") {
        next.immeubleId = "";
        next.bienId = "";
      }
      if (key === "immeubleId") {
        next.bienId = "";
        const immeuble = immeubles.find((item) => item.id === value);
        if (immeuble) next.proprietaireId = immeuble.proprietaireId;
      }
      if (key === "bienId") {
        const bien = getBien(value);
        if (bien) {
          next.immeubleId = bien.immeubleId;
          next.proprietaireId = bien.proprietaireId;
        }
      }
      return next;
    });
  };

  const handlePhotos = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;
    const remaining = MAX_PHOTOS - photos.length;
    const selected = files.slice(0, Math.max(0, remaining));
    const urls = await Promise.all(selected.map((file) => readFileAsDataUrl(file)));
    setPhotos((prev) => [...prev, ...urls]);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const resetAndClose = () => {
    setForm(EMPTY_STATE);
    setPhotos([]);
    setErrors({});
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors: Record<string, string> = {};
    if (!form.probleme.trim()) nextErrors.probleme = "Décrivez le problème constaté.";
    if (!form.date) nextErrors.date = "Indiquez la date de l'intervention.";

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    const input: AgenceInterventionInput = {
      proprietaireId: form.proprietaireId,
      immeubleId: form.immeubleId,
      bienId: form.bienId,
      probleme: form.probleme.trim(),
      description: form.description.trim(),
      priorite: form.priorite,
      statut: form.statut,
      photos,
      cout: Math.max(0, Number(form.cout) || 0),
      prestataire: form.prestataire.trim(),
      date: form.date,
    };

    if (initial) {
      updateIntervention(initial.id, input);
    } else {
      addIntervention(input);
    }
    resetAndClose();
  };

  return (
    <Modal
      open={open}
      onClose={resetAndClose}
      size="lg"
      title={initial ? "Modifier l'intervention" : "Ajouter une intervention"}
      description="Décrivez le problème, sa priorité et le prestataire. Vous pourrez suivre son statut depuis sa fiche."
      footer={
        <>
          <button
            type="button"
            onClick={resetAndClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Annuler
          </button>
          <button
            type="submit"
            form="agence-intervention-form"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-check-line text-base" aria-hidden="true" />
            Enregistrer l'intervention
          </button>
        </>
      }
    >
      <form id="agence-intervention-form" onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
          <Field label="Problème" htmlFor="agence-int-probleme" required error={errors.probleme} className="md:col-span-2">
            <Input
              id="agence-int-probleme"
              value={form.probleme}
              onChange={(event) => update("probleme", event.target.value)}
              placeholder="Ex. Fuite d'eau dans la salle de bain"
              invalid={Boolean(errors.probleme)}
              maxLength={140}
            />
          </Field>

          <Field label="Description" htmlFor="agence-int-description" className="md:col-span-2">
            <Textarea
              id="agence-int-description"
              value={form.description}
              onChange={(event) => update("description", event.target.value)}
              placeholder="Détail du problème, travaux à prévoir, remarques..."
              className="min-h-[96px]"
              maxLength={600}
            />
          </Field>

          <Field label="Propriétaire" htmlFor="agence-int-proprietaire" hint="Facultatif, filtre les immeubles.">
            <Select
              id="agence-int-proprietaire"
              value={form.proprietaireId}
              onChange={(event) => update("proprietaireId", event.target.value)}
              options={proprietaireOptions}
              placeholder={
                proprietaireOptions.length > 0 ? "Aucun propriétaire en particulier" : "Aucun propriétaire créé"
              }
              disabled={proprietaireOptions.length === 0}
            />
          </Field>

          <Field label="Immeuble" htmlFor="agence-int-immeuble" hint="Facultatif.">
            <Select
              id="agence-int-immeuble"
              value={form.immeubleId}
              onChange={(event) => update("immeubleId", event.target.value)}
              options={immeubleOptions}
              placeholder={immeubleOptions.length > 0 ? "Aucun immeuble en particulier" : "Aucun immeuble disponible"}
              disabled={immeubleOptions.length === 0}
            />
          </Field>

          <Field label="Bien" htmlFor="agence-int-bien" hint="Facultatif, filtré selon l'immeuble.">
            <Select
              id="agence-int-bien"
              value={form.bienId}
              onChange={(event) => update("bienId", event.target.value)}
              options={bienOptions}
              placeholder={bienOptions.length > 0 ? "Aucun bien en particulier" : "Aucun bien disponible"}
              disabled={bienOptions.length === 0}
            />
          </Field>

          <Field label="Priorité" htmlFor="agence-int-priorite" required>
            <Select
              id="agence-int-priorite"
              value={form.priorite}
              onChange={(event) => update("priorite", event.target.value)}
              options={AGENCE_INTERVENTION_PRIORITE_OPTIONS}
            />
          </Field>

          <Field label="Statut" htmlFor="agence-int-statut" required>
            <Select
              id="agence-int-statut"
              value={form.statut}
              onChange={(event) => update("statut", event.target.value)}
              options={AGENCE_INTERVENTION_STATUT_OPTIONS}
            />
          </Field>

          <Field label="Prestataire" htmlFor="agence-int-prestataire" hint="Nom de l'entreprise ou de la personne.">
            <Input
              id="agence-int-prestataire"
              value={form.prestataire}
              onChange={(event) => update("prestataire", event.target.value)}
              placeholder="Ex. Plomberie Wend-Panga"
              maxLength={120}
            />
          </Field>

          <Field label="Coût (FCFA)" htmlFor="agence-int-cout">
            <Input
              id="agence-int-cout"
              type="number"
              min={0}
              max={100000000}
              value={form.cout}
              onChange={(event) => update("cout", event.target.value)}
              placeholder="Ex. 35000"
            />
          </Field>

          <Field label="Date" htmlFor="agence-int-date" required error={errors.date}>
            <Input
              id="agence-int-date"
              type="date"
              value={form.date}
              onChange={(event) => update("date", event.target.value)}
              invalid={Boolean(errors.date)}
            />
          </Field>

          <Field
            label="Photos"
            htmlFor="agence-int-photos"
            hint={`Jusqu'à ${MAX_PHOTOS} photos (aperçu local, l'envoi réel viendra avec le stockage).`}
            className="md:col-span-2"
          >
            <div className="flex flex-col gap-3">
              {photos.length > 0 && (
                <div className="flex flex-wrap gap-3">
                  {photos.map((photo, index) => (
                    <div
                      key={`${photo.slice(0, 24)}-${index}`}
                      className="group relative h-20 w-20 overflow-hidden rounded-md border border-background-200 bg-background-100"
                    >
                      <img src={photo} alt={`Photo ${index + 1}`} className="h-20 w-20 object-cover object-top" />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        aria-label={`Retirer la photo ${index + 1}`}
                        className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-md bg-foreground-950/60 text-background-50 cursor-pointer"
                      >
                        <i className="ri-close-line text-sm" aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={photos.length >= MAX_PHOTOS}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer"
                >
                  <i className="ri-upload-2-line text-base" aria-hidden="true" />
                  Ajouter des photos
                </button>
                <span className="text-xs text-foreground-500">
                  {photos.length}/{MAX_PHOTOS}
                </span>
              </div>
              <input
                ref={fileRef}
                id="agence-int-photos"
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotos}
                className="hidden"
              />
            </div>
          </Field>
        </div>
      </form>
    </Modal>
  );
}